import React, { useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import AddressesFilters from "../components/Addresses/AddressesFilters";
import AddressesTable from "../components/Addresses/AddressesTable";
import LocationIcon from "../assets/images/location_pin_rgb_red.svg";
import PaginationComponent from "../components/PaginationComponent";
import AddressesButtons from "../components/Addresses/AddressesButtons";
import "../assets/styles/addresses.scss";
import api from "../services/httpClient";

const Addresses = () => {
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [filters, setFilters] = useState({});
  const filtersRef = useRef({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: "Id", direction: "asc" });
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberedTotalCount, setRememberedTotalCount] = useState(null);
  // Bump this to trigger a new fetch; starts at 0 – first fetch is triggered by location.search effect.
  const [fetchVersion, setFetchVersion] = useState(0);
  const initialMountRef = useRef(true);

  const handleSelectionChange = (newSelectedIds) => {
    setSelectedIds(newSelectedIds);
  };
  const [data, setData] = useState({
    items: [],
    itemsCount: null,
    totalRecords: 0,
    totalPages: 0,
    pageNumber: 1,
    pageSize: itemsPerPage,
  });

  // Refs so the fetch effect always reads latest values without needing them as deps.
  const currentPageRef = useRef(currentPage);
  const itemsPerPageRef = useRef(itemsPerPage);
  const appliedFiltersRef = useRef(appliedFilters);
  const sortConfigRef = useRef(sortConfig);
  filtersRef.current = filters;

  currentPageRef.current = currentPage;
  itemsPerPageRef.current = itemsPerPage;
  appliedFiltersRef.current = appliedFilters;
  sortConfigRef.current = sortConfig;

  // Guard against StrictMode double-invocation.
  const lastFetchedVersionRef = useRef(0);

  // Single fetch effect – fires only when fetchVersion changes.
  // Cancels the previous in-flight request when a new one starts.
  const abortControllerRef = useRef(null);

  React.useEffect(() => {
    if (lastFetchedVersionRef.current === fetchVersion) return;
    lastFetchedVersionRef.current = fetchVersion;

    // Anuluj poprzedni request jeśli jeszcze trwa
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    let cancelled = false;
    const pageNumber = currentPageRef.current;
    const pageSize = itemsPerPageRef.current;
    const filtersToApply = appliedFiltersRef.current;
    const sort = sortConfigRef.current;

    (async () => {
      setIsLoading(true);
      try {
        const response = await api.get(`/Address/List`, {
          params: {
            pageNumber,
            pageSize,
            SortBy: sort.key,
            SortDirection: sort.direction,
            ...filtersToApply,
          },
          signal: controller.signal,
        });

        if (cancelled) return;

        const responsePageNumber = Number(response.data.pageNumber || pageNumber);
        const responseTotalCountRaw =
          response.data.totalCount ??
          response.data.itemsCount ??
          response.data.totalRecords;
        const responseTotalCount =
          Number.isFinite(Number(responseTotalCountRaw)) &&
          Number(responseTotalCountRaw) >= 0
            ? Number(responseTotalCountRaw)
            : null;

        if (responsePageNumber === 1) {
          setRememberedTotalCount(responseTotalCount);
        }

        setData({
          items: Array.isArray(response.data.items) ? response.data.items : [],
          itemsCount: responseTotalCount,
          totalRecords: response.data.totalRecords || 0,
          totalPages: response.data.totalPages || 0,
          pageNumber: response.data.pageNumber || 1,
          pageSize: response.data.pageSize || pageSize,
        });
      } catch (error) {
        if (cancelled || error?.name === "CanceledError" || error?.name === "AbortError") return;
        console.error("Error fetching data:", error);
        setData({
          items: [],
          itemsCount: null,
          totalRecords: 0,
          totalPages: 0,
          pageNumber: 1,
          pageSize: pageSize,
        });
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [fetchVersion]);

  React.useEffect(() => {
    if (initialMountRef.current) {
      initialMountRef.current = false;

      const params = new URLSearchParams(location.search);
      const incomingFilters = {};
      params.forEach((value, key) => { incomingFilters[key] = value; });

      setFilters(incomingFilters);
      filtersRef.current = incomingFilters;
      setAppliedFilters(incomingFilters);
      setCurrentPage(1);
      setRememberedTotalCount(null);
      // Zawsze triggeruj pierwszy fetch
      setTimeout(() => setFetchVersion((v) => v + 1), 0);
      return;
    }

    const params = new URLSearchParams(location.search);
    const incomingFilters = {};

    params.forEach((value, key) => {
      incomingFilters[key] = value;
    });

    setFilters(incomingFilters);
    filtersRef.current = incomingFilters;
    setAppliedFilters(incomingFilters);
    setCurrentPage(1);
    setRememberedTotalCount(null);
    setFetchVersion((v) => v + 1);
  }, [location.search]);

  const handleFilterChange = (field, value) => {
    setFilters((prevFilters) => {
      const nextFilters = {
        ...prevFilters,
        [field]: value,
      };
      filtersRef.current = nextFilters;
      return nextFilters;
    });
  };

  const handleSearch = () => {
    setAppliedFilters(filtersRef.current);
    setCurrentPage(1);
    setRememberedTotalCount(null);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handleSortChange = (newSortConfig) => {
    setSortConfig(newSortConfig);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handlePageChange = (page) => {
    if (Number(page) === Number(currentPageRef.current)) return;
    setCurrentPage(page);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handleItemsPerPageChange = (pageSize) => {
    setItemsPerPage(pageSize);
    setCurrentPage(1);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  return (
    <div className="addresses-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img
              src={LocationIcon}
              alt="Location Pin"
              style={{ width: "30px", height: "30px" }}
            />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              Warstwa adresowa
            </h3>
          </Col>
          <Col xs="auto">
            <AddressesButtons
              onSearch={handleSearch}
              selectedIds={selectedIds}
              filters={filters}
            />
          </Col>
        </Row>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <AddressesFilters
              onFilterChange={handleFilterChange}
              onSearch={handleSearch}
              filters={filters}
            />
          </Col>
          <Col className="flex-grow-1" />
        </Row>
        <Row>
          <Col>
            <AddressesTable
              data={data.items}
              isLoading={isLoading}
              onSortChange={handleSortChange}
              onSelectionChange={handleSelectionChange}
            />
          </Col>
        </Row>
        <Row className="align-items-center mt-4">
          <PaginationComponent
            itemsPerPage={data.pageSize}
            dataCount={data.items.length}
            itemsCount={data.itemsCount ?? rememberedTotalCount}
            totalCount={rememberedTotalCount}
            pageNumber={data.pageNumber}
            totalPages={data.totalPages}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[ 20, 100, 500]}
          />
        </Row>
      </Container>
    </div>
  );
};

export default Addresses;
